import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import posthog from 'posthog-js';
import API_URL from '../config';
import { mergeSubscriptionTier } from '../utils/subscription';
import { SovrynnLogo } from './Brand';
import '../App.css';
import '../pages/legal-pages.css';

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        {
          email: formData.email,
          password: formData.password,
        }
      );

      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem(
          'user',
          JSON.stringify(mergeSubscriptionTier(response.data.user)),
        );

        const user = mergeSubscriptionTier(response.data.user);
        posthog.identify(user.email, {
          email: user.email,
          name: user.firstName + ' ' + user.lastName,
          plan: user.subscriptionTier || 'free',
          createdAt: user.createdAt,
        });
        posthog.group('plan', user.subscriptionTier || 'free', {
          name: user.subscriptionTier || 'free',
        });

        // Check onboarding status from the API response user object
        if (user.onboardingCompleted === false || user.onboardingCompleted === undefined) {
          navigate('/onboarding');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      const isNetworkError =
        err.code === 'ERR_NETWORK' ||
        err.code === 'ERR_CONNECTION_REFUSED' ||
        err.message === 'Network Error';
      setError(
        isNetworkError
          ? 'Cannot reach the server. Make sure the backend is running (npm run start:dev in the project root).'
          : (err.response?.data?.message || 'Invalid email or password. Please try again.')
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container-wrapper">
      <div className="login-container">
        <div className="login-content">
          {/* Sovrynn Logo — prominent, always visible */}
          <div className="auth-logo">
            <SovrynnLogo size="lg" />
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="login-form">
          {/* Email Input */}
          <div className="input-group">
            <label
              htmlFor="email"
              className={`form-label ${emailFocused || formData.email ? 'focused' : ''}`}
            >
              Email
            </label>
            <input
              type="email"
              name="email"
              id="email"
              value={formData.email}
              onChange={handleChange}
              onFocus={() => setEmailFocused(true)}
              onBlur={() => setEmailFocused(formData.email.length > 0)}
              className="form-input"
              placeholder="you@example.com"
              required
            />
          </div>

          {/* Password Input */}
          <div className="input-group">
            <label
              htmlFor="password"
              className={`form-label ${passwordFocused || formData.password ? 'focused' : ''}`}
            >
              Password
            </label>
            <input
              type="password"
              name="password"
              id="password"
              value={formData.password}
              onChange={handleChange}
              onFocus={() => setPasswordFocused(true)}
              onBlur={() => setPasswordFocused(formData.password.length > 0)}
              className="form-input"
              placeholder="••••••••"
              required
            />
          </div>

          <div className="login-forgot-password-row">
            <Link to="/forgot-password" className="login-forgot-password-link">
              Forgot your password?
            </Link>
          </div>

          {/* Error Message */}
          {error && <div className="error-message">{error}</div>}

          {/* Submit Button */}
          <button
            type="submit"
            className="submit-button"
            disabled={isLoading}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>

          {/* Register Link */}
          <div className="register-link">
            <span>Don't have an account? </span>
            <Link to="/register" className="register-link-text">
              Register
            </Link>
          </div>

          <div className="login-legal-links">
            <Link to="/terms">Terms of Service</Link>
            {' · '}
            <Link to="/privacy">Privacy Policy</Link>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default Login;

